import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { getReminders } from "@/lib/api";
import { fmtDate } from "@/components/common";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const TYPE_LABEL = { facilities: "Facilities", vehicle: "Fleet" };

const DueItem = ({ it }) => {
  const meta = [TYPE_LABEL[it.type] || it.type, it.sublabel].filter(Boolean).join(" · ");
  const to = it.section + (it.category ? `?tab=${it.category}` : "");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="due-item" className="w-full text-left flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
          <div className="min-w-0">
            <div className="font-medium truncate">{it.label || "—"}</div>
            <div className="text-sm text-gray-500 truncate">
              {meta}
              {it.due_date ? ` · due ${fmtDate(it.due_date)}` : ""}
            </div>
          </div>
          <span
            className={`shrink-0 inline-block text-xs font-bold uppercase tracking-wide px-3 py-1 ${
              it.urgency === "overdue" ? "bg-[#D50000] text-white" : "bg-[#FFD600] text-black"
            }`}
          >
            {it.urgency === "overdue" ? "Overdue" : "Due"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="rounded-none border-2 border-gray-900 w-80">
        <div className="space-y-3" data-testid="due-item-detail">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-gray-400">{TYPE_LABEL[it.type] || it.type}</div>
            <div className="font-head font-bold text-lg uppercase tracking-tight text-gray-900">{it.label || "—"}</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <span className="text-gray-400 uppercase text-xs font-bold tracking-wide">Needs</span>
            <span className="col-span-2 font-medium text-gray-900">{it.work || "—"}</span>
            {it.sublabel && <><span className="text-gray-400 uppercase text-xs font-bold tracking-wide">Where</span><span className="col-span-2 text-gray-700">{it.sublabel}</span></>}
            <span className="text-gray-400 uppercase text-xs font-bold tracking-wide">When</span>
            <span className="col-span-2 text-gray-700">{it.due_date ? fmtDate(it.due_date) : (it.urgency === "overdue" ? "Overdue" : "This month")}</span>
          </div>
          {it.detail && (
            <div className="border-t border-gray-100 pt-2">
              <div className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-1">Notes</div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">{it.detail}</div>
            </div>
          )}
          <Link
            to={to}
            data-testid="due-item-link"
            className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-[#16265F] hover:underline"
          >
            View in {TYPE_LABEL[it.type] || it.type} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function DueThisMonth({ filterType = null, title = "Due This Month" }) {
  const { data } = useQuery({ queryKey: ["reminders"], queryFn: getReminders, refetchInterval: 5000 });
  let items = data?.items || [];
  if (filterType) items = items.filter((i) => i.type === filterType);
  if (!items.length) return null;

  return (
    <div data-testid="due-this-month" className="border-2 border-gray-900 bg-white">
      <div className="flex items-center gap-2 px-5 py-3 bg-[#16265F] text-white">
        <AlertTriangle className="w-5 h-5 text-[#E0A81E]" />
        <span className="font-head font-bold uppercase tracking-wide">{title}</span>
        <span className="ml-2 text-white/60 text-sm">{items.length} item{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="divide-y divide-gray-100">
        {items.map((it, i) => (
          <DueItem key={i} it={it} />
        ))}
      </div>
    </div>
  );
}
